import { Alert, Skeleton } from "@mui/material";
import {
    useGetCCFromTemplateQuery,
    useLazyGetExpertCCDataQuery,
    useUpdateExpertCCDataMutation,
} from "modules/pvs_activities/redux/service/dForm";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { FormDataContext } from "../contexts/FormContext";
import TextAreaField from "../core/TextAreaField";
import DropdownField from "../core/DropdownField";
import { useGetMissionExpertsQuery } from "modules/pvs_activities/redux/service/missionExperts";
import { debounce } from "lodash";
import { displayNotification } from "helpers";

const ExpertsCCTable = (props) => {
    const formDataContext = useContext(FormDataContext);
    const siteLanguage = useSelector((s) => s.general.siteLanguage);

    const ccListRes = useGetCCFromTemplateQuery({
        formId: formDataContext.templateId,
        languageId: 3,
        missionId: formDataContext.missionId,
    });
    const missionExpertsRes = useGetMissionExpertsQuery(
        {
            missionId: formDataContext.missionId,
            attributes: "id,expert_name,expert_id",
            is_active: "1",
            status: "1",
            responseStatus: 1,
        },
        { skip: formDataContext.mode === "preview" }
    );
    const [data, setData] = useState({});
    const [expertCCDataTrigger, expertCCDataRes] = useLazyGetExpertCCDataQuery();
    const [updateExpertCCData, updateExpertCCDataRes] = useUpdateExpertCCDataMutation();

    useEffect(() => {
        if (ccListRes.isSuccess) {
            if (formDataContext.mode === "data_entry") {
                expertCCDataTrigger({
                    formId: formDataContext.templateId,
                    elementId: props.id,
                    movementId: formDataContext.movementId,
                });
            }
            const tmp = {};
            for (const cc of ccListRes.data) {
                tmp[cc.id] = {
                    template_id: formDataContext.templateId,
                    section_id: formDataContext.sectionId,
                    element_id: props.id,
                    movement_id: formDataContext.movementId,
                    mission_id: formDataContext.missionId,
                    cc_id: cc.id,
                    status: "",
                    expert_id: "",
                };
            }
            setData(tmp);
        }
    }, [ccListRes]);

    useEffect(() => {
        if (expertCCDataRes.isError) {
            displayNotification("error", "Error loading expert cc data.");
            console.error({ error: expertCCDataRes.error });
        }
        if (expertCCDataRes.isSuccess) {
            const tmp = { ...data };
            for (const r in expertCCDataRes.data) {
                tmp[r] = {
                    ...tmp[r],
                    ...expertCCDataRes.data[r],
                };
            }
            setData(tmp);
        }
    }, [expertCCDataRes]);

    useEffect(() => {
        if (updateExpertCCDataRes.isError) {
            displayNotification("error", "Error saving expert cc data.");
            console.error({ error: updateExpertCCDataRes.error });
        }
    }, [updateExpertCCDataRes]);

    const expertsOptions = useMemo(() => {
        if (missionExpertsRes.status === "uninitialised") return [];
        if (missionExpertsRes.isLoading) return [{ value: "", label: "Please wait! Loading..." }];
        if (missionExpertsRes.isError) return [{ value: "", label: "Error Loading data." }];
        if (missionExpertsRes.isSuccess)
            return missionExpertsRes.data.map((r) => ({ value: r.expert_id, label: r.expert_name }));
        return [];
    }, [missionExpertsRes]);

    const handleChange = (ccId, field, val) => {
        const tmp = { ...data, [ccId]: { ...data[ccId], [field]: val } };
        setData(tmp);
        if (formDataContext.mode === "data_entry") {
            updateValueToParent(tmp[ccId]);
        }
    };

    const updateValueToParent = useCallback(
        debounce((obj) => {
            updateExpertCCData(obj);
        }, 1000),
        [props.handleChange]
    );

    return ccListRes.isLoading ||
        (formDataContext.mode === "data_entry" &&
            (expertCCDataRes.isLoading || expertCCDataRes.status === "uninitialised")) ? (
        <>
            <Skeleton variant="rounded" width={"100%"} height={200} sx={{ my: 1 }} />
        </>
    ) : ccListRes.isError || expertCCDataRes.isError ? (
        <>
            <Alert severity="warning" sx={{ my: 1 }}>
                Error loading Critical Competency List
            </Alert>
        </>
    ) : ccListRes.isSuccess &&
      (formDataContext.mode === "preview" || (formDataContext.mode === "data_entry" && expertCCDataRes.isSuccess)) ? (
        <>
            <table className="table table-bordered border-dark">
                <thead>
                    <tr className="bg-primary text-white">
                        <th className="text-center">PVS Summary results of {"{COUNTRY_NAME}"}</th>
                        <th className="text-center" width="20%">
                            Status
                        </th>
                        <th className="text-center" width="20%">
                            Expert Name
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {ccListRes.data.map((r) => (
                        <tr key={r.id} className={`${r.is_cc ? "" : "bg-light-3"}`}>
                            <td
                                colSpan={r.is_cc === 0 ? 3 : 1}
                                style={{ fontWeight: r.is_cc === 0 ? "bold" : "normal" }}
                            >
                                {siteLanguage === "3"
                                    ? r.complete_name_en
                                    : siteLanguage === "4"
                                    ? r.complete_name_fr
                                    : siteLanguage === "5"
                                    ? r.complete_name_es
                                    : r.complete_name}
                            </td>
                            {r.is_cc === 1 && (
                                <>
                                    <td className="text-center">
                                        <TextAreaField
                                            label={"Status"}
                                            type={"text"}
                                            value={data[r.id]?.status}
                                            onChange={(val) => handleChange(r.id, "status", val)}
                                            className="bg-white"
                                            size="small"
                                        />
                                    </td>
                                    <td className="text-center">
                                        <DropdownField
                                            label={"Expert Name"}
                                            plain={true}
                                            hasOptions={true}
                                            options={expertsOptions}
                                            value={data[r.id]?.expert_id}
                                            onChange={(val) => handleChange(r.id, "expert_id", val)}
                                            className="bg-white"
                                            size="small"
                                        />
                                    </td>
                                </>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </>
    ) : (
        ""
    );
};

ExpertsCCTable.defaultProps = {
    id: "",
    element: {},
    isReadOnly: true,
};

export default ExpertsCCTable;
